// UploadDropzone (DESIGN §3.4.8): `field` (Formular: Fläche `tile` 120 hoch, Radius 16, gestrichelt
// `line`, UploadSimple 24, „Drop a file or browse“, Typen und Größe `small` `fg-secondary`, AA auf `tile`) ·
// `overlay` (der Inhalt wird beim Ziehen zur Ablagefläche: Rand 2 px gestrichelt `accent`, Fläche
// `accent-soft`, Mitte „Drop to upload to Policies“) · `avatar` (Kreis 96 mit Overlay „Change“).
// Prüft Typ und Größe; Fehler in `danger-fg`, Ergebnis über `role="status"`. Als Knopf fokussierbar,
// Enter/Leertaste öffnet den Dateidialog. Den Upload selbst (Fortschritt W5) macht der Aufrufer.
import { useId, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { CameraIcon, UploadSimpleIcon, WarningCircleIcon } from '@phosphor-icons/react';
import {
  checkFiles,
  describeAccept,
  dragHasFiles,
  formatBytes,
  type FileRules,
} from './_lib/files';
import { cn } from '../basis/kern';

export interface UploadDropzoneProps extends FileRules {
  /** Geprüfte Dateien (abgelehnte stehen als Meldung unter dem Feld). */
  onFiles: (files: File[]) => void;
  variant?: 'field' | 'overlay' | 'avatar';
  multiple?: boolean;
  /** `field`: Haupttext. */
  label?: string;
  /** `field`: Typen/Größe; Standard aus `accept` und `maxSize`. */
  hint?: string;
  /** `overlay`: Zielname in „Drop to upload to …“. */
  targetName?: string;
  /** `overlay`: der Bereich, der zur Ablagefläche wird (z. B. die Dateiliste). */
  children?: ReactNode;
  /** `avatar`: der aktuelle Avatar (96). */
  avatar?: ReactNode;
  /** Ergebnis nach dem Hochladen, z. B. „2 files uploaded“ (role="status"). */
  status?: ReactNode;
  disabled?: boolean;
  className?: string;
}

export function UploadDropzone({
  onFiles,
  variant = 'field',
  multiple = variant !== 'avatar',
  accept,
  maxSize,
  label = multiple ? 'Drop files or browse' : 'Drop a file or browse',
  hint,
  targetName,
  children,
  avatar,
  status,
  disabled = false,
  className,
}: UploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const hintId = useId();
  const [dragging, setDragging] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  // Zähler gegen Flackern: dragenter/dragleave feuern auch beim Wechsel über Kind-Elemente.
  const depth = useRef(0);
  const hintText =
    hint ??
    [describeAccept(accept), maxSize !== undefined ? `up to ${formatBytes(maxSize)}` : null]
      .filter(Boolean)
      .join(' · ');

  const take = (list: FileList | null) => {
    const result = checkFiles(Array.from(list ?? []), { accept, maxSize }, multiple);
    setErrors(result.errors);
    if (result.accepted.length) onFiles(result.accepted);
  };

  const drag = {
    onDragEnter: (event: DragEvent<HTMLElement>) => {
      if (disabled || !dragHasFiles(event.dataTransfer?.types)) return;
      event.preventDefault();
      depth.current += 1;
      setDragging(true);
    },
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (disabled || !dragHasFiles(event.dataTransfer?.types)) return;
      event.preventDefault();
    },
    onDragLeave: () => {
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragging(false);
    },
    onDrop: (event: DragEvent<HTMLElement>) => {
      if (disabled) return;
      event.preventDefault();
      depth.current = 0;
      setDragging(false);
      take(event.dataTransfer?.files ?? null);
    },
  };

  const input = (
    <input
      ref={inputRef}
      type="file"
      hidden
      multiple={multiple}
      accept={accept?.join(',')}
      disabled={disabled}
      onChange={(event) => {
        take(event.target.files);
        event.target.value = '';
      }}
    />
  );
  const feedback = (
    <>
      {errors.length ? (
        <ul className="flex flex-col gap-1 text-label text-danger-fg">
          {errors.map((message) => (
            <li key={message} className="flex items-start gap-1.5">
              <WarningCircleIcon size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
              {message}
            </li>
          ))}
        </ul>
      ) : null}
      <div role="status" className="text-label text-fg-secondary empty:hidden">
        {status}
      </div>
    </>
  );

  if (variant === 'overlay') {
    return (
      <div {...drag} data-dragging={dragging || undefined} className={cn('relative', className)}>
        {children}
        {dragging ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-(--bar-z-sticky) flex items-center justify-center rounded-xl border-2 border-dashed border-accent bg-accent-soft/60"
          >
            <span className="text-title-detail text-fg">
              {targetName ? `Drop to upload to ${targetName}` : 'Drop to upload'}
            </span>
          </div>
        ) : null}
        {feedback}
      </div>
    );
  }

  if (variant === 'avatar') {
    return (
      <div className={cn('flex flex-col items-start gap-2', className)}>
        <button
          type="button"
          {...drag}
          disabled={disabled}
          aria-describedby={hintId}
          onClick={() => inputRef.current?.click()}
          data-dragging={dragging || undefined}
          className={cn(
            'group/avatar relative size-24 shrink-0 overflow-hidden rounded-full disabled:cursor-not-allowed',
            'data-[dragging=true]:ring-2 data-[dragging=true]:ring-accent data-[dragging=true]:ring-offset-2 data-[dragging=true]:ring-offset-page',
          )}
        >
          <span className="pointer-events-none inline-flex size-24 [&>*]:size-24">{avatar}</span>
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-scrim text-label text-on-brand opacity-0 transition-opacity duration-(--bar-dur-fast) ease-standard group-hover/avatar:opacity-100 group-focus-visible/avatar:opacity-100 group-data-[dragging=true]/avatar:opacity-100">
            <CameraIcon size={20} aria-hidden="true" />
            Change
          </span>
        </button>
        <span id={hintId} className="text-small text-fg-secondary">
          {hintText}
        </span>
        {input}
        {feedback}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <button
        type="button"
        {...drag}
        disabled={disabled}
        aria-describedby={hintId}
        onClick={() => inputRef.current?.click()}
        data-dragging={dragging || undefined}
        className={cn(
          'group/drop flex h-30 w-full flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-line bg-tile px-4 text-center',
          'transition-colors duration-(--bar-dur-fast) ease-standard hover:not-disabled:bg-tile-hover',
          'data-[dragging=true]:border-2 data-[dragging=true]:border-accent data-[dragging=true]:bg-accent-soft',
          'disabled:cursor-not-allowed disabled:text-fg-disabled',
        )}
      >
        <UploadSimpleIcon
          size={24}
          aria-hidden="true"
          className="text-fg-secondary group-disabled/drop:text-fg-disabled"
        />
        <span className="text-body text-fg group-disabled/drop:text-fg-disabled">
          {dragging ? 'Drop to upload' : label}
        </span>
        <span
          id={hintId}
          className="text-small text-fg-secondary group-disabled/drop:text-fg-disabled"
        >
          {hintText}
        </span>
      </button>
      {input}
      {feedback}
    </div>
  );
}
